import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card'
import { Field, FieldError, FieldGroup, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { changeAppLanguage, isAppLanguageCode } from '@repo/i18n'
import {
	postBackofficeAuthGetMe,
	postBackofficeAuthSignOut,
	postBackofficeAuthUpdateMe,
	postBackofficeAuthUpdateMePreferences
} from '@repo/sdk/gen'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check, Languages, LogOut, Palette } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import {
	MutationErrorAlert,
	QueryErrorState,
	QueryLoadingState
} from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { useTheme } from '@repo/ui/commons/theme-provider'

const accountSchema = z.object({
	language: z.enum(['en', 'it']),
	name: z.string().trim().min(1).max(100),
	theme: z.enum(['light', 'dark', 'system'])
})

type AccountForm = z.infer<typeof accountSchema>

export const Route = createFileRoute('/account')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	component: AccountPage
})

function AccountPage() {
	const { i18n, t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { setTheme, theme } = useTheme()
	const currentLanguage = i18n.resolvedLanguage ?? i18n.language
	const language = isAppLanguageCode(currentLanguage) ? currentLanguage : 'en'
	const profileQuery = useQuery({
		queryKey: ['backoffice', 'profile'],
		queryFn: () =>
			postBackofficeAuthGetMe({ body: {} }).then(
				(response) => response.data.data
			)
	})
	const hasProfile = profileQuery.data !== undefined
	const profileName = profileQuery.data?.name ?? ''
	const form = useForm<AccountForm>({
		defaultValues: { language, name: '', theme },
		resolver: zodResolver(accountSchema)
	})
	const saveMutation = useMutation({
		mutationFn: (values: AccountForm) =>
			Promise.all([
				postBackofficeAuthUpdateMe({
					body: { data: { name: values.name } }
				}),
				postBackofficeAuthUpdateMePreferences({
					body: { language: values.language, theme: values.theme }
				})
			]),
		onSuccess: async (_, values) => {
			setTheme(values.theme)
			await changeAppLanguage(values.language)
		},
		onSettled: async () => {
			await queryClient.invalidateQueries({
				queryKey: ['backoffice', 'profile']
			})
		}
	})
	const signOutMutation = useMutation({
		mutationFn: () => postBackofficeAuthSignOut({ body: {} }),
		onSuccess: async () => {
			queryClient.clear()
			await navigate({ to: '/login' })
		}
	})

	useEffect(() => {
		if (!hasProfile) {
			return
		}

		form.reset({
			language,
			name: profileName,
			theme
		})
	}, [form, hasProfile, language, profileName, theme])

	return (
		<BackofficeShell contentClassName='max-w-3xl'>
			<header>
				<p className='font-medium text-primary text-sm'>
					{t('common:backoffice')}
				</p>
				<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
					{t('common:account')}
				</h1>
				<p className='mt-3 max-w-xl text-muted-foreground'>
					{t('common:account_copy')}
				</p>
			</header>
			{profileQuery.isError ? (
				<QueryErrorState
					message={t('common:profile_load_error')}
					onRetry={() => profileQuery.refetch()}
				/>
			) : null}
			<Card className='mt-8 rounded-2xl'>
				<CardHeader>
					<CardTitle>{t('common:profile')}</CardTitle>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={form.handleSubmit((values) =>
							saveMutation.mutate(values)
						)}
					>
						<FieldGroup>
							{profileQuery.isPending ? (
								<QueryLoadingState
									message={t('common:loading')}
								/>
							) : null}
							{saveMutation.isError ? (
								<MutationErrorAlert />
							) : null}
							<Controller
								control={form.control}
								name='name'
								render={({ field, fieldState }) => (
									<Field data-invalid={fieldState.invalid}>
										<FieldLabel htmlFor={field.name}>
											{t('common:display_name')}
										</FieldLabel>
										<Input
											{...field}
											aria-invalid={fieldState.invalid}
											id={field.name}
										/>
										<FieldError
											errors={[fieldState.error]}
										/>
									</Field>
								)}
							/>
							<Field>
								<FieldLabel htmlFor='account-email'>
									{t('common:email')}
								</FieldLabel>
								<Input
									disabled
									id='account-email'
									readOnly
									type='email'
									value={profileQuery.data?.email ?? ''}
								/>
							</Field>
							<Controller
								control={form.control}
								name='language'
								render={({ field }) => (
									<Field>
										<FieldLabel>
											<Languages className='size-4' />
											{t('common:language')}
										</FieldLabel>
										<div className='flex gap-2'>
											<PreferenceButton
												active={field.value === 'en'}
												onClick={() =>
													field.onChange('en')
												}
											>
												English
											</PreferenceButton>
											<PreferenceButton
												active={field.value === 'it'}
												onClick={() =>
													field.onChange('it')
												}
											>
												Italiano
											</PreferenceButton>
										</div>
									</Field>
								)}
							/>
							<Controller
								control={form.control}
								name='theme'
								render={({ field }) => (
									<Field>
										<FieldLabel>
											<Palette className='size-4' />
											{t('common:appearance')}
										</FieldLabel>
										<div className='flex flex-wrap gap-2'>
											{(
												[
													'system',
													'light',
													'dark'
												] as const
											).map((themeOption) => (
												<PreferenceButton
													active={
														field.value ===
														themeOption
													}
													key={themeOption}
													onClick={() =>
														field.onChange(
															themeOption
														)
													}
												>
													{t(
														`common:theme_${themeOption}`
													)}
												</PreferenceButton>
											))}
										</div>
									</Field>
								)}
							/>
						</FieldGroup>
						<div className='mt-6 flex justify-end'>
							<Button
								disabled={saveMutation.isPending || !hasProfile}
								type='submit'
							>
								<Check />
								{t('common:save_changes')}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
			<Card className='mt-6 rounded-2xl'>
				<CardContent className='flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between'>
					<div>
						<h2 className='font-semibold'>{t('common:session')}</h2>
						<p className='mt-1 text-muted-foreground text-sm'>
							{t('common:session_copy')}
						</p>
					</div>
					<Button
						disabled={signOutMutation.isPending}
						onClick={() => signOutMutation.mutate()}
						variant='outline'
					>
						<LogOut />
						{t('common:sign_out')}
					</Button>
				</CardContent>
			</Card>
		</BackofficeShell>
	)
}

function PreferenceButton(props: {
	active: boolean
	children: string
	onClick: () => void
}) {
	return (
		<Button
			onClick={props.onClick}
			type='button'
			variant={props.active ? 'default' : 'outline'}
		>
			{props.children}
		</Button>
	)
}
